/**
 * Which client (tenant) the interface is working on, for every call.
 *
 * A person can reach more than one tenant (document/61 M4): their home one,
 * bound to their Security organization, and every tenant where they hold a
 * CMS membership. The gateway picks among them with the `X-Tenant` header;
 * without it the request runs against the home tenant. The choice is kept
 * here, in one place, so `apiFetch` and `apiUpload` add the header to every
 * call and no screen can forget it.
 *
 * Remembered in this browser only, as a convenience: storage may be missing
 * or blocked (private windows, previews), and then the interface simply
 * starts from the home tenant again. The home tenant itself is never stored:
 * "no header" is its honest spelling, and it keeps working even if the
 * person's organization is later bound elsewhere.
 */

export const TENANT_HEADER = 'X-Tenant'
export const TENANT_STORAGE_KEY = 'cms.workspace.tenantId'

/** The shape the gateway accepts in `X-Tenant` (apps/gateway/src/auth.ts): anything else answers 400 on every call. */
const TENANT_ID = /^[A-Za-z0-9_-]{1,128}$/

/** `undefined` until the first read: storage is touched once, not on every request. */
let selected: string | null | undefined

export function isTenantId(value: unknown): value is string {
  return typeof value === 'string' && TENANT_ID.test(value)
}

/** The tenant picked in this browser, or null for the home tenant. */
export function readSelectedTenant(): string | null {
  if (selected !== undefined) return selected
  try {
    const stored = window.localStorage.getItem(TENANT_STORAGE_KEY)
    selected = isTenantId(stored) ? stored : null
  } catch {
    selected = null
  }
  return selected
}

/** Null goes back to the home tenant. Losing the stored copy only costs one more click next time. */
export function saveSelectedTenant(tenantId: string | null): void {
  selected = isTenantId(tenantId) ? tenantId : null
  try {
    if (selected) window.localStorage.setItem(TENANT_STORAGE_KEY, selected)
    else window.localStorage.removeItem(TENANT_STORAGE_KEY)
  } catch {
    // A preference, not state: the choice still holds for this page.
  }
}

/** The headers every authenticated call carries: `X-Tenant` only when a tenant other than home is picked. */
export function tenantHeaders(): Record<string, string> {
  const tenantId = readSelectedTenant()
  return tenantId ? { [TENANT_HEADER]: tenantId } : {}
}

/** Tests only: forget the value read from storage so the next read goes back to it. */
export function forgetTenantSelectionCache(): void {
  selected = undefined
}
